import { ref, onMounted } from "vue";
import { api } from "@app/data/api";
import { RestaurantSettingKey } from "@lemmonhq/types";
import { getRestaurantDateKey } from "@lemmonhq/blogic";
import { useAuth } from "@app/vue/providers/Auth";
import { useToast } from "@app/vue/providers/Toast";

export function usePauseReservations() {
  const auth = useAuth();
  const toast = useToast();

  const reservationsPaused = ref(false);
  const reservationsPauseLoading = ref(false);

  const getTimezone = () => auth.user.value?.restaurant?.timezone || "UTC";

  const getTodayStr = () => getRestaurantDateKey(new Date(), getTimezone());

  const loadPauseState = async () => {
    if (reservationsPauseLoading.value) return;
    try {
      const res = auth.user.value?.restaurant_settings || [];
      const vacationSetting = res.find(
        (s) => s.key === RestaurantSettingKey.VACATION_SCHEDULE,
      );
      if (vacationSetting?.value) {
        const parsed =
          typeof vacationSetting.value === "string"
            ? JSON.parse(vacationSetting.value)
            : vacationSetting.value;
        const items = Array.isArray(parsed) ? parsed : parsed?.items || [];
        const todayStr = getTodayStr();
        reservationsPaused.value = items.some(
          (entry: any) =>
            entry.date === todayStr && entry.reason === "paused_by_manager",
        );
      } else {
        reservationsPaused.value = false;
      }
    } catch (e) {
      console.error("Failed to load pause state", e);
    }
  };

  const handleTogglePause = async () => {
    try {
      reservationsPauseLoading.value = true;
      const todayStr = getTodayStr();

      const res = await api.getSettings();
      const vacationSetting = res.data.find(
        (s) => s.key === RestaurantSettingKey.VACATION_SCHEDULE,
      );
      let schedule: any[] = [];
      if (vacationSetting?.value) {
        const parsed =
          typeof vacationSetting.value === "string"
            ? JSON.parse(vacationSetting.value)
            : vacationSetting.value;
        schedule = Array.isArray(parsed) ? parsed : parsed?.items || [];
      }

      // Remove stale paused_by_manager entries
      schedule = schedule.filter(
        (entry: any) =>
          !(entry.reason === "paused_by_manager" && entry.date < todayStr),
      );

      if (reservationsPaused.value) {
        schedule = schedule.filter(
          (entry: any) =>
            !(entry.date === todayStr && entry.reason === "paused_by_manager"),
        );
      } else {
        schedule.push({
          date: todayStr,
          reason: "paused_by_manager",
          payload: { type: "closed_all_day" },
        });
      }

      const resp = await api.bulkUpdateSettings(
        {
          settings: [
            {
              key: RestaurantSettingKey.VACATION_SCHEDULE,
              value: JSON.stringify({ items: schedule }),
              value_type: "json",
            },
          ],
        },
        auth,
      );

      reservationsPaused.value = !reservationsPaused.value;
      toast.showToast(
        reservationsPaused.value
          ? "Reservations paused for today"
          : "Reservations resumed for today",
        { type: "success", autoHide: 3000 },
      );
    } catch (e) {
      console.error("Failed to toggle pause", e);
      toast.showToast("Failed to toggle pause", {
        type: "error",
        autoHide: 3000,
      });
    } finally {
      reservationsPauseLoading.value = false;
    }
  };

  onMounted(loadPauseState);

  return { reservationsPaused, reservationsPauseLoading, handleTogglePause };
}
